import React, { useState } from 'react';
import {
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  ArrowsPointingOutIcon,
  SunIcon,
  CommandLineIcon,
  SpeakerWaveIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useAccessibility } from '../../hooks/useAccessibility';
import { announceToScreenReader } from '../../utils/a11yHelpers';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    textSize,
    setTextSize,
    highContrast,
    setHighContrast,
    keyboardNavHelper,
    setKeyboardNavHelper,
    screenReaderMode,
    setScreenReaderMode,
    resetAccessibility,
    stopSpeaking
  } = useAccessibility();

  const toggleWidget = () => {
    setIsOpen(!isOpen);
  };

  const textSizeOptions = [
    { value: 'normal', label: 'רגיל', percentage: '100%' },
    { value: 'large', label: 'גדול', percentage: '125%' },
    { value: 'xlarge', label: 'גדול מאוד', percentage: '150%' }
  ];

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={toggleWidget}
        className="fixed left-4 bottom-4 z-50 bg-primary-600 hover:bg-primary-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-primary-300"
        aria-label={isOpen ? 'סגור תפריט נגישות' : 'פתח תפריט נגישות'}
        aria-expanded={isOpen}
        aria-controls="accessibility-panel"
      >
        {isOpen ? (
          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
        ) : (
          <AdjustmentsHorizontalIcon className="h-6 w-6" aria-hidden="true" />
        )}
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <div
          id="accessibility-panel"
          className="fixed left-4 bottom-20 z-50 w-80 bg-white rounded-lg shadow-2xl border border-gray-200"
          role="dialog"
          aria-label="הגדרות נגישות"
        >
          {/* Header */}
          <div className="bg-primary-600 text-white px-6 py-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <AdjustmentsHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                הגדרות נגישות
              </h2>
              <button
                onClick={toggleWidget}
                className="text-white hover:bg-primary-700 p-1 rounded transition-colors"
                aria-label="סגור"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 max-h-96 overflow-y-auto">
            {/* Text Size */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                <ArrowsPointingOutIcon className="h-5 w-5 text-primary-600" aria-hidden="true" />
                גודל טקסט
              </label>
              <div className="grid grid-cols-3 gap-2">
                {textSizeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setTextSize(option.value)}
                    className={`px-3 py-2 rounded-lg border-2 transition-all ${
                      textSize === option.value
                        ? 'border-primary-600 bg-primary-50 text-primary-900 font-semibold'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-primary-300'
                    }`}
                    aria-pressed={textSize === option.value}
                  >
                    <div className="text-xs">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.percentage}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* High Contrast */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <SunIcon className="h-5 w-5 text-primary-600" aria-hidden="true" />
                ניגודיות גבוהה
              </label>
              <button
                onClick={() => setHighContrast(!highContrast)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  highContrast ? 'bg-primary-600' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={highContrast}
                aria-label="החלף מצב ניגודיות גבוהה"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    highContrast ? 'translate-x-1' : 'translate-x-6'
                  }`}
                />
              </button>
            </div>

            {/* Keyboard Navigation Helper */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <CommandLineIcon className="h-5 w-5 text-primary-600" aria-hidden="true" />
                עזרת ניווט במקלדת
              </label>
              <button
                onClick={() => setKeyboardNavHelper(!keyboardNavHelper)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  keyboardNavHelper ? 'bg-primary-600' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={keyboardNavHelper}
                aria-label="החלף עזרת ניווט במקלדת"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    keyboardNavHelper ? 'translate-x-1' : 'translate-x-6'
                  }`}
                />
              </button>
            </div>

            {/* Keyboard Navigation Shortcuts */}
            {keyboardNavHelper && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-gray-900">
                <p className="font-semibold mb-2 text-yellow-900">קיצורי מקלדת זמינים:</p>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <span>ניווט קדימה</span>
                    <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono">Tab</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>ניווט אחורה</span>
                    <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono">Shift + Tab</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>הפעלה/לחיצה</span>
                    <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono">Enter / Space</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>סגירת חלונות</span>
                    <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono">Esc</kbd>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>גלילה למעלה/מטה</span>
                    <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-mono">↑ ↓</kbd>
                  </div>
                </div>
                <p className="mt-2 text-yellow-800 text-xs italic">
                  💡 רמז: כל האלמנטים האינטראקטיביים מודגשים בצהוב, ותראה tooltips כשמרחף עליהם
                </p>
              </div>
            )}

            {/* Screen Reader Mode */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <SpeakerWaveIcon className="h-5 w-5 text-primary-600" aria-hidden="true" />
                מצב קורא מסך
              </label>
              <button
                onClick={() => {
                  const newValue = !screenReaderMode;
                  setScreenReaderMode(newValue);
                  const message = newValue 
                    ? 'מצב קורא מסך הופעל. כל האנימציות בוטלו, ואלמנטים אינטראקטיביים מודגשים'
                    : 'מצב קורא מסך כבוי';
                  announceToScreenReader(message, 'polite');
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  screenReaderMode ? 'bg-primary-600' : 'bg-gray-300'
                }`}
                role="switch"
                aria-checked={screenReaderMode}
                aria-label="החלף מצב קורא מסך"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    screenReaderMode ? 'translate-x-1' : 'translate-x-6'
                  }`}
                />
              </button>
            </div>
            
            {/* Screen Reader Mode Description */}
            {screenReaderMode && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
                <p className="font-semibold mb-2">מצב קורא מסך פעיל:</p>
                <ul className="list-disc list-inside space-y-1 mb-2">
                  <li>כל האנימציות והמעברים בוטלו</li>
                  <li>אלמנטים אינטראקטיביים מודגשים בקו מקווקו כחול</li>
                  <li>תמונות דקורטיביות מוצגות בשקיפות נמוכה</li>
                  <li>טקסטים עזר נוספים מוצגים</li>
                  <li><strong>קריאה בקול:</strong> מרחף על כפתור/קישור כדי לקרוא</li>
                </ul>
                <button
                  onClick={stopSpeaking}
                  className="w-full px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium"
                  aria-label="עצור קריאה בקול"
                >
                  🔇 עצור קריאה
                </button>
              </div>
            )}

            {/* Reset Button */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  resetAccessibility();
                  // Announce to screen reader
                  const announcement = document.createElement('div');
                  announcement.setAttribute('role', 'status');
                  announcement.setAttribute('aria-live', 'polite');
                  announcement.className = 'sr-only';
                  announcement.textContent = 'הגדרות הנגישות אופסו';
                  document.body.appendChild(announcement);
                  setTimeout(() => document.body.removeChild(announcement), 1000);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                אפס הגדרות
              </button>
            </div>

            {/* Info */}
            <div className="pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-600 text-center">
                הגדרות אלו נשמרות באופן אוטומטי במכשיר שלך
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overlay (for mobile) */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={toggleWidget}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default AccessibilityWidget;

